/* FAQ accordion search + deep links for the collapsible-content section.
   Adapted from the startsafety_us theme's <accordion-groups> component:
   Dawn wraps each <details> in a bordered .accordion div, so items are the
   [data-details-group] wrappers and open/close targets the inner <details>. */

(() => {
const DEBOUNCE_MS = 150;
const MIN_CHARS = 2;
const HIGHLIGHT_CLASS = 'accordion-search-highlight';

const normalize = (text) => text.replace(/\s+/g, ' ').trim().toLowerCase();

const debounce = (fn, wait) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

// True when ch is a letter/number, i.e. the highlight runs into the rest of a
// word rather than sitting at a word boundary.
const isWordChar = (ch) => ch != null && /[\p{L}\p{N}]/u.test(ch);

function highlightTerms(root, terms) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentNode && node.parentNode.nodeName !== 'MARK'
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  });
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    const lower = text.toLowerCase();
    const ranges = [];
    for (const term of terms) {
      if (!term) continue;
      let i = 0;
      while ((i = lower.indexOf(term, i)) !== -1) {
        ranges.push([i, i + term.length]);
        i += term.length;
      }
    }
    if (!ranges.length) return;

    ranges.sort((a, b) => a[0] - b[0]);
    const merged = [ranges[0].slice()];
    for (let k = 1; k < ranges.length; k++) {
      const last = merged[merged.length - 1];
      const [start, end] = ranges[k];
      if (start <= last[1]) last[1] = Math.max(last[1], end);
      else merged.push([start, end]);
    }

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    for (const [start, end] of merged) {
      if (start > cursor) {
        fragment.appendChild(document.createTextNode(text.slice(cursor, start)));
      }
      const mark = document.createElement('mark');
      mark.className = HIGHLIGHT_CLASS;
      mark.textContent = text.slice(start, end);
      if (isWordChar(text[start - 1])) mark.style.setProperty('--mark-pad-start', '0');
      if (isWordChar(text[end])) mark.style.setProperty('--mark-pad-end', '0');
      fragment.appendChild(mark);
      cursor = end;
    }
    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }
    node.parentNode.replaceChild(fragment, node);
  });
}

function clearHighlights(root) {
  root.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((mark) => {
    const parent = mark.parentNode;
    parent.replaceChild(document.createTextNode(mark.textContent), mark);
    parent.normalize();
  });
}

function setDetailsOpen(detailsEl, open) {
  if (!detailsEl) return;
  detailsEl.open = open;
  const summary = detailsEl.querySelector('summary');
  if (summary) summary.setAttribute('aria-expanded', open);
}

class AccordionGroups extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-accordion-search]');
    if (!this.input) return;

    this.countEl = this.querySelector('[data-accordion-search-count]');
    this.noResultsEl = this.querySelector('[data-accordion-no-results]');
    this.resetButton = this.querySelector('[data-accordion-search-reset]');
    this.items = Array.from(this.querySelectorAll('[data-details-group]')).map((el) => {
      const detailsEl = el.tagName === 'DETAILS' ? el : el.querySelector('details');
      const titleEl = el.querySelector('[data-accordion-title]');
      const bodyEl = el.querySelector('[data-accordion-body]');
      return {
        el,
        detailsEl,
        titleEl,
        bodyEl,
        title: normalize(titleEl ? titleEl.textContent : ''),
        body: normalize(bodyEl ? bodyEl.textContent : ''),
      };
    });

    this.categories = Array.from(this.querySelectorAll('[data-accordion-category]'));
    this.categoryNav = this.querySelector('[data-accordion-nav]');

    this.debouncedFilter = debounce(() => this.filter(), DEBOUNCE_MS);
    this.input.addEventListener('input', () => {
      this.toggleResetButton();
      this.debouncedFilter();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    if (this.resetButton) {
      this.resetButton.addEventListener('click', () => {
        this.clearSearch();
        this.input.focus();
      });
    }

  }

  /* Hides a category heading once every row beneath it has been filtered out. */
  syncCategories() {
    this.categories.forEach((heading) => {
      let node = heading.nextElementSibling;
      let hasVisibleRow = false;
      while (node && !node.hasAttribute('data-accordion-category')) {
        if (node.hasAttribute('data-details-group') && !node.hidden) hasVisibleRow = true;
        node = node.nextElementSibling;
      }
      heading.hidden = !hasVisibleRow;
    });
    if (this.categoryNav) this.categoryNav.hidden = this.input.value.length >= MIN_CHARS;
  }

  toggleResetButton() {
    if (this.resetButton) this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
  }

  clearSearch() {
    this.input.value = '';
    this.reset();
    this.toggleResetButton();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.clearSearch();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const firstVisible = this.items.find((item) => !item.el.hidden);
      if (firstVisible) setDetailsOpen(firstVisible.detailsEl, true);
    }
  }

  filter() {
    const query = normalize(this.input.value);
    if (query.length < MIN_CHARS) {
      this.reset();
      return;
    }

    const terms = query.split(' ').filter(Boolean);
    let matchCount = 0;
    let lastMatch = null;

    this.items.forEach((item) => {
      clearHighlights(item.el);
      const haystack = `${item.title} ${item.body}`;
      const matches = terms.every((term) => haystack.includes(term));
      item.el.hidden = !matches;
      setDetailsOpen(item.detailsEl, false);
      if (matches) {
        matchCount++;
        lastMatch = item;
        if (item.titleEl) highlightTerms(item.titleEl, terms);
        if (item.bodyEl) highlightTerms(item.bodyEl, terms);
      }
    });

    if (matchCount === 1 && lastMatch) setDetailsOpen(lastMatch.detailsEl, true);

    if (this.countEl) {
      this.countEl.textContent =
        matchCount > 0 ? `${matchCount} result${matchCount === 1 ? '' : 's'}` : '';
    }
    if (this.noResultsEl) this.noResultsEl.hidden = matchCount > 0;
    this.syncCategories();
  }

  reset() {
    this.items.forEach((item) => {
      clearHighlights(item.el);
      item.el.hidden = false;
      setDetailsOpen(item.detailsEl, false);
    });
    if (this.countEl) this.countEl.textContent = '';
    if (this.noResultsEl) this.noResultsEl.hidden = true;
    this.syncCategories();
  }
}

if (!customElements.get('accordion-groups')) {
  customElements.define('accordion-groups', AccordionGroups);
}

// Deep links: /pages/faqs#do-i-need-to-install-signs opens and scrolls to that
// question. Without JS the browser still scrolls to the (closed) row.
function openAnchoredQuestion() {
  const hash = decodeURIComponent(window.location.hash.slice(1));
  if (!hash) return;
  const target = document.getElementById(hash);
  if (!target || !target.hasAttribute('data-details-group')) return;
  setDetailsOpen(target.tagName === 'DETAILS' ? target : target.querySelector('details'), true);
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

openAnchoredQuestion();
window.addEventListener('hashchange', openAnchoredQuestion);
})();
